// AES-CBC encryption matching the backend's generateKey / encryptPayloadUserId.
// Key = SHA-256(userId + ENCRYPTION_PASSWORD + salt)
// For public (unauthenticated) submissions: userId = "", salt = "default_salt"
// (backend does `env.SALT || 'default_salt'` — empty string is falsy, so default_salt wins)

async function deriveBackendKey(password: string, usage: 'encrypt' | 'decrypt'): Promise<CryptoKey> {
  const keyInput = password + 'default_salt'
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(keyInput),
  )
  return crypto.subtle.importKey('raw', hashBuffer, { name: 'AES-CBC' }, false, [usage])
}

export async function encryptForBackend(data: object, password: string): Promise<string> {
  const key = await deriveBackendKey(password, 'encrypt')
  const iv = crypto.getRandomValues(new Uint8Array(16))
  const encoded = new TextEncoder().encode(JSON.stringify(data))
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, encoded)

  const combined = new Uint8Array(16 + encrypted.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(encrypted), 16)

  // Backend uses chunk-based String.fromCharCode to avoid call stack overflow
  let result = ''
  for (let i = 0; i < combined.length; i += 10000) {
    result += String.fromCharCode(...combined.slice(i, i + 10000))
  }
  return btoa(result)
}

export async function decryptFromBackend<T = unknown>(token: string, password: string): Promise<T> {
  const key = await deriveBackendKey(password, 'decrypt')
  const buf = Uint8Array.from(atob(token), (c) => c.charCodeAt(0))
  const iv = buf.slice(0, 16)
  const ciphertext = buf.slice(16)
  const plain = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext)
  return JSON.parse(new TextDecoder().decode(plain)) as T
}
